import type { PropsWithChildren, FunctionComponent } from 'react'
import { useEffect, useState, useMemo } from 'react'
import { matchPath } from 'react-router'
import { routes } from '@retailer-platform/domain-instacart-developer-platform'
import { colors } from '@retailer-platform/shared-components'
import ErrorBoundary from '../../../legacy/components/ErrorBoundary'
import FeatureEducationModal from '../../../legacy/components/FeatureHighlights/FeatureEducationModal'
import useFeatureEducation from '../../../legacy/components/FeatureHighlights/useFeatureEducation'
import { AppContentContainer } from '../../../gin-and-tonic/containers/app-content-container/AppContentContainer'
import { UserFeedbackButton } from '../../../gin-and-tonic/containers/user-feedback/UserFeedbackButton'
import { AiButtonFloating } from '../../../gin-and-tonic/common/ai-button-floating/AiButtonFloating'
import { LeftShortcutsPaneRetailer } from '../../../gin-and-tonic/containers/nav-v2/components/LeftShortcutsPane/LeftShortcutsPaneRetailer'
import { MenuBarTop } from '../../../gin-and-tonic/containers/nav-v2/components/MenuBarTop'
import { useNavHierarchyRetailer } from '../../../gin-and-tonic/containers/nav-v2/NavMenuHierarchy'
import { useWindowSizeIsWide } from '../../../utils/window/windows.hooks'
import { ContactHeader } from '../../utils/ContactHeader'
import { useRequiredNavDataLoadingStatus } from '../../../gin-and-tonic/containers/nav/utils/nav.hooks'
import { DASH_NAV_V2_HEIGHT } from '../../../legacy/common/theme'
import { useNavContext } from '../../../gin-and-tonic/containers/nav/utils/NavContext'
import { DashRoute } from '../../../legacy/dash-components/dash-route/DashRoute'
import { DashSwitch } from '../../../legacy/dash-components/dash-switch/DashSwitch'
import { DashRedirect } from '../../../legacy/dash-components/dash-redirect/DashRedirect'
import { HaltTerms } from '../../../sections/halt/components/terms/HaltTerms'
import { usePartnerContext, URL_PARTNER_HALT_TERMS } from '../../../exports/routing'
import { url } from '../../../utils/parsing/url'
import { useFetchCurrentEnterpriseAccountQuery, AgreementTypeEnum } from '../../../__codegen__/api'
import { AttachPartnerAnalyticsMetadata } from '../../utils/AttachAnalyticsMetadata'
import { PartnerType } from '../../../exports/utils'
import {
  consumeStoredRedirectUrl,
  storeCurrentRedirectUrl,
  TERMS_REDIRECT_KEY,
} from '../utils/loggedIn.utils'
import { URL_DASHBOARD } from '../../../utils/routing/routes'
import { useAiAgent } from '../../../utils/contexts/ai/AiAgentContext'
import { AiAgentChatBar } from '../../../utils/contexts/ai/AiAgentChatBar'
import { RetailerScopedSection } from './retailer-scoped/RetailerScopedSection'
import { PartnerScopedRoutes } from './PartnerScopedRoutes'
interface Props {
  pathname: string
}

export const PartnerScopedApp: FunctionComponent<PropsWithChildren<Props>> = ({ pathname }) => {
  const isWide = useWindowSizeIsWide()
  const { isNavVisible, isSideNavVisible } = useNavContext()
  const partnerContext = usePartnerContext()
  const { isChatOpen, isAgentEnabled } = useAiAgent()
  const {
    data,
    loading: accountLoading,
    refetch,
  } = useFetchCurrentEnterpriseAccountQuery({
    fetchPolicy: 'cache-and-network',
  })
  const [redirectUrl] = useState<string | null>(() => consumeStoredRedirectUrl(TERMS_REDIRECT_KEY))

  // temporarily gate behind roulette
  const showIdpTerms = !!data.currentEnterpriseAccount.featureToggles?.find(
    feature => feature.name === 'rt_idp_terms'
  )

  const showDashboardTerms = !!data.currentEnterpriseAccount.featureToggles?.find(
    feature => feature.name === 'rt_dashboard_terms'
  )

  const { isOpen, onOpen, onClose, onClick, feature } = useFeatureEducation(pathname)
  const expectedPartnerAgreementType =
    partnerContext.partnerType === PartnerType.IDP
      ? AgreementTypeEnum.IdpV1
      : AgreementTypeEnum.PartnerDashboardV1

  useEffect(() => {
    if (isOpen) {
      onOpen()
    }
  }, [isOpen, onOpen, pathname])

  const { navMenuHierarchy } = useNavHierarchyRetailer()

  const termsAccepted = useMemo(
    () =>
      data?.currentEnterpriseAccount.acceptedPartnerAgreements.some(
        agreement => agreement.agreementType === expectedPartnerAgreementType
      ),
    [data?.currentEnterpriseAccount.acceptedPartnerAgreements, expectedPartnerAgreementType]
  )

  const isImpersonationSession = data?.currentEnterpriseAccount.isImpersonating

  const shouldRedirectToTerms = useMemo(
    () =>
      ((showIdpTerms && partnerContext.partnerType === PartnerType.IDP) ||
        (showDashboardTerms && partnerContext.partnerType === PartnerType.Retailer)) &&
      !termsAccepted &&
      !isImpersonationSession &&
      !matchPath(pathname, { path: URL_PARTNER_HALT_TERMS }),
    [
      showIdpTerms,
      showDashboardTerms,
      partnerContext.partnerType,
      termsAccepted,
      pathname,
      isImpersonationSession,
    ]
  )

  const shouldConsumeTermsRedirectUrl = useMemo(
    () => matchPath(pathname, { path: URL_PARTNER_HALT_TERMS }) && termsAccepted,
    [pathname, termsAccepted]
  )

  const { loading, error } = useRequiredNavDataLoadingStatus()
  if (loading || accountLoading || error) {
    return null
  }

  if (shouldRedirectToTerms) {
    storeCurrentRedirectUrl(TERMS_REDIRECT_KEY)
    return (
      <DashRedirect
        to={`${url(URL_PARTNER_HALT_TERMS, {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          partner_id: partnerContext.partnerId,
        })}`}
      />
    )
  }

  if (shouldConsumeTermsRedirectUrl) {
    const defaultUrl =
      partnerContext.partnerType === PartnerType.IDP
        ? url(routes['instacart-developer-platform-get-started'], {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            partner_id: partnerContext.partnerId,
          })
        : url(URL_DASHBOARD, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            partner_id: partnerContext.partnerId,
          })

    return <DashRedirect to={redirectUrl || defaultUrl} />
  }

  const topPadding = isNavVisible ? DASH_NAV_V2_HEIGHT : '0px'
  const leftPadding = '0px'

  return (
    <>
      <DashSwitch>
        <DashRoute
          route="partner-halt-terms"
          render={() => <HaltTerms refetchCurrentAccount={refetch} />}
          exact
        />
        <DashRoute
          route="partner-scoped"
          render={() => (
            <>
              {isNavVisible && <MenuBarTop navMenuHierarchy={navMenuHierarchy} />}
              <AttachPartnerAnalyticsMetadata />
              <UserFeedbackButton />
              {isAgentEnabled && !isChatOpen && <AiButtonFloating />}
              <div
                css={{
                  display: 'flex',

                  // this CSS is a relic of the Nav V1 implementation. If we don't position absolute left: 0
                  // the content is shifted off screen to the left.
                  position: 'absolute',
                  left: leftPadding,

                  width: `calc(100% - ${leftPadding})`,
                  minHeight: `calc(100% - ${topPadding})`,
                  height: `calc(100% - ${topPadding})`,
                }}
              >
                {isNavVisible && isSideNavVisible && isWide && <LeftShortcutsPaneRetailer />}

                <FeatureEducationModal
                  isOpen={isOpen}
                  onClose={onClose}
                  onClick={onClick}
                  feature={feature}
                />

                <div
                  css={{
                    display: 'flex',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    overflowY: 'hidden',
                    // Dynamically adjust the container width to account for chat panel
                    width: isChatOpen ? 'calc(100% - 325px)' : '100%',
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <AppContentContainer css={{ flex: 1, minWidth: 0 }}>
                    <ContactHeader />
                    <ErrorBoundary pathname={pathname}>
                      <DashSwitch>
                        <DashRoute route="retailer-scoped" component={RetailerScopedSection} />
                        <PartnerScopedRoutes />
                      </DashSwitch>
                    </ErrorBoundary>
                  </AppContentContainer>

                  {isAgentEnabled && (
                    <div
                      css={{
                        width: isChatOpen ? '325px' : '0px',
                        flexShrink: 0,
                        borderLeft: isChatOpen ? `1px solid ${colors.GRAYSCALE.X20}` : 'none',
                        backgroundColor: colors.GRAYSCALE.X0,
                        overflow: 'hidden',
                        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      <div css={{ width: '325px', height: '100%' }}>
                        <AiAgentChatBar />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        />
      </DashSwitch>
    </>
  )
}
